import { useState, useEffect, useCallback } from 'react';
import { useDataContext } from '../data/DataProvider';
import {
  getSessionUser,
  onAuthChange,
  isSignedInUser,
  sendAccountCode,
  confirmAccountCode,
  sendSignInCode,
  confirmSignInCode,
  signOutToGuest,
  startGoogleLink,
  startGoogleSignIn,
  startAppleSignIn,
  confirmInviteToken,
} from '../lib/authAccount';
import { isDemoAddress, requestDemoSignIn } from '../lib/demoSignIn';
import { track } from '../utils/analytics';
import { isNative, isIOS } from '../utils/platformUtils';

// Toutes les couches ne rendent pas la meme forme d'erreur : Supabase renvoie
// tantot un Error avec message, tantot un objet dont la serialisation donne "{}".
// L'UI affichait cet objet tel quel, si bien qu'un utilisateur bloque voyait "{}"
// alors que le message existait ("Error sending email change email"). On prend le
// premier texte exploitable, et a defaut une phrase utile plutot qu'un objet vide.
// Supabase repond dans sa langue a lui. Telle quelle, une reponse comme "Signups
// not allowed for otp" ne dit rien a un musicien : elle veut dire qu'aucun compte
// n'existe pour cette adresse. On traduit les cas que l'utilisateur rencontrera
// forcement, et on laisse passer le reste plutot que de masquer un message utile.
const FRIENDLY_ERRORS = [
  [/signups not allowed/i, 'No account found with this email. Check the address.'],
  [/rate limit|too many requests/i, 'Too many attempts. Wait a minute and try again.'],
  [
    /token has expired|invalid.*token|otp.*expired/i,
    'That verification code has expired. Ask for a new one.',
  ],
  [/user already registered|already been registered/i, 'This email already has an account. Sign in instead.'],
];

// L'adresse porte deja un compte : la creation est refusee, mais ce n'est pas
// une impasse, c'est la reponse du serveur a la question "creer ou connecter".
// Meme lecture double que isIdentityTaken dans authAccount : Supabase le dit
// tantot par un code, tantot seulement en clair dans le message.
function isEmailTaken(err) {
  const code = err?.code || err?.error_code;
  if (code === 'email_exists' || code === 'user_already_exists') return true;
  return /already (been )?registered|email.*already exists|already exists/i.test(err?.message || '');
}

export function readErrorMessage(err, fallback = 'Something went wrong. Please try again.') {
  if (!err) return fallback;
  const raw =
    typeof err === 'string'
      ? err
      : [err.message, err.error_description, err.error, err.msg, err.details].find(
          (value) => typeof value === 'string' && value.trim()
        );
  if (!raw || !raw.trim()) return fallback;

  const friendly = FRIENDLY_ERRORS.find(([pattern]) => pattern.test(raw));
  return friendly ? friendly[1] : raw.trim();
}

// Logique de compte, partagee par la feuille d'inscription (SignUpSheet) et la
// section Account des Settings. Elle vivait en double dans AccountSection : le
// meme formulaire y etait a la fois la page d'inscription et le panneau de
// gestion, ce qui obligeait a le retrouver dans les reglages pour s'inscrire.
//
// Deux chemins d'email, comme dans authAccount :
//  - 'create' : updateUser({email}) sur la session anonyme. Meme user_id, donc
//    tout ce qui a ete fait en invite (reglages, patterns, code beta) reste.
//  - 'signin' : signInWithOtp sur un compte existant. La session est remplacee
//    et les donnees locales de l'invite sont purgees, d'ou le rechargement.
//
// Google suit le mode : 'create' rattache l'identite a la session courante
// (startGoogleLink), et si l'email a deja un compte, useOAuthReturn bascule tout
// seul en connexion pure ; 'signin' part directement en connexion pure
// (startGoogleSignIn), un seul aller-retour, un seul selecteur de compte.
//
// `variant` est celle de la feuille appelante (SignUpSheet). Elle ne sert qu'à
// une chose ici : savoir si un achat est en jeu. Quand il y en a un, le repli
// silencieux création -> connexion n'est pas acceptable, cf. pendingSignInEmail.
export function useAccountAuth({ variant, onAccountAuthenticated } = {}) {
  const { db } = useDataContext();
  const [user, setUser] = useState(null);
  const [mode, setMode] = useState('create'); // 'create' | 'signin'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [justSignedUp, setJustSignedUp] = useState(false);
  // Adresse qui porte déjà un compte, en attente d'une décision explicite de
  // l'utilisateur. Un état à part et non une troisième valeur de `mode` : mode
  // est lu par sendCode, verifyCode et continueWithGoogle, une valeur de plus y
  // ouvrirait trois branches mortes.
  const [pendingSignInEmail, setPendingSignInEmail] = useState(null);

  // Les deux seules variantes où un achat est déjà posé sur l'appareil, ou sur
  // le point de l'être. Ailleurs, la bascule silencieuse ne coûte rien et rend
  // le service pour lequel elle a été écrite.
  const warnBeforeSignIn = variant === 'purchase' || variant === 'before_purchase';

  useEffect(() => {
    let active = true;
    getSessionUser().then((u) => {
      if (active) setUser(u);
    });
    const unsubscribe = onAuthChange((u) => {
      if (active) setUser(u);
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  const signedIn = isSignedInUser(user);

  const resetForm = useCallback(() => {
    setCode('');
    setCodeSent(false);
    setError(null);
    setNotice(null);
    setJustSignedUp(false);
    setPendingSignInEmail(null);
  }, []);

  const resetAll = useCallback(() => {
    setMode('create');
    setEmail('');
    resetForm();
    setBusy(false);
  }, [resetForm]);

  // Ouvre directement le chemin connexion. Sert au cas ou la creation de compte
  // n'a aucun sens : l'utilisateur affirme avoir deja achete, lui presenter un
  // formulaire d'inscription reviendrait a lui demander de repartir de zero.
  const startSignIn = useCallback(() => {
    setMode('signin');
    setEmail('');
    resetForm();
  }, [resetForm]);

  const switchMode = useCallback(() => {
    setMode((m) => (m === 'create' ? 'signin' : 'create'));
    setEmail('');
    resetForm();
  }, [resetForm]);

  const showCodeStep = useCallback((value) => {
    setCodeSent(true);
    setNotice(`We sent a 6-digit code to ${value}. Enter it below.`);
  }, []);

  // Rend true quand le code est parti. Sert aux appelants qui mesurent l'envoi
  // (page d'invitation) ; les autres ignorent la valeur.
  const sendCode = useCallback(async () => {
    setError(null);
    setNotice(null);
    const value = email.trim();
    if (!value || !value.includes('@')) {
      setError('Please enter a valid email address.');
      return false;
    }
    setBusy(true);
    try {
      if (mode === 'create') {
        // Une seule porte "par email" : c'est le serveur qui tranche creation ou
        // connexion, pas l'utilisateur. Demander "avez-vous deja un compte ?" a
        // quelqu'un qui a recu son acces par mail, c'est lui demander de deviner
        // ce que Supabase sait deja. Le repli ne va que dans ce sens : partir de
        // 'signin' (variant restore) pour creer un compte vide sur une adresse
        // mal tapee ferait payer deux fois quelqu'un qui a deja paye.
        try {
          await sendAccountCode(value);
        } catch (err) {
          if (!isEmailTaken(err)) throw err;
          // Sauf quand un achat est en jeu. Basculer en connexion enchaîne
          // confirmSignInCode -> switchIdentity -> clearAllUserData : les
          // données locales sont purgées et l'achat reste sur l'identité
          // anonyme qu'on vient de quitter. Quelqu'un qui vient de payer ne
          // peut pas découvrir ça après coup, il doit le décider avant. Rien
          // n'est envoyé ici : le code de connexion ne part qu'à la
          // confirmation, sinon l'auto-vérification le présenterait à
          // confirmAccountCode, qui ne l'attend pas.
          if (warnBeforeSignIn) {
            setPendingSignInEmail(value);
            return false;
          }
          await sendSignInCode(value);
          setMode('signin');
        }
      } else {
        await sendSignInCode(value);
      }
      showCodeStep(value);
      return true;
    } catch (err) {
      // Adresse de review des stores : son code ne vient pas du mail, il est
      // fixe. Un envoi refuse (quota Supabase apres plusieurs essais) ne doit
      // donc pas cacher le champ de saisie, sinon le reviewer est bloque a
      // l'entree et l'app est rejetee.
      if (!isDemoAddress(value)) {
        setError(readErrorMessage(err));
        return false;
      }
      showCodeStep(value);
      return true;
    } finally {
      setBusy(false);
    }
  }, [email, mode, showCodeStep, warnBeforeSignIn]);

  // L'utilisateur a lu l'avertissement et veut quand même se connecter. C'est le
  // seul endroit qui bascule en connexion depuis une variante d'achat, et la
  // bascule et l'envoi tiennent ensemble : un code de connexion sans le mode qui
  // va avec partirait à confirmAccountCode.
  const confirmSignInSwitch = useCallback(async () => {
    const value = pendingSignInEmail;
    if (!value) return false;
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await sendSignInCode(value);
      setPendingSignInEmail(null);
      setMode('signin');
      showCodeStep(value);
      return true;
    } catch (err) {
      // On reste sur l'avertissement avec le message : rien n'a basculé, la
      // décision est encore à prendre.
      setError(readErrorMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  }, [pendingSignInEmail, showCodeStep]);

  // Retour en arrière. L'adresse n'est pas effacée : elle est peut-être juste
  // mal tapée, et l'utilisateur retombe sur l'étape email pour la corriger.
  const cancelSignInSwitch = useCallback(() => {
    setPendingSignInEmail(null);
    setError(null);
    setNotice(null);
  }, []);

  // Rend true quand le compte est pret cote appelant (creation). En mode
  // 'signin' la page se recharge : rien ne revient jamais.
  const verifyCode = useCallback(async () => {
    setError(null);
    setNotice(null);
    if (!code.trim()) {
      setError('Enter the verification code from your email.');
      return false;
    }
    setBusy(true);
    try {
      // Compte de review des stores, avant Supabase : le serveur echange la
      // paire fixe contre un jeton de connexion (src/lib/demoSignIn.js). Filtre
      // sur le domaine, donc rien ne part pour une adresse ordinaire, et un
      // refus (ou un serveur muet) laisse le chemin Supabase se derouler comme
      // si de rien n'etait -- aucun message particulier n'existe pour ce cas.
      const demoToken = await requestDemoSignIn(email.trim(), code.trim());
      if (demoToken) {
        await confirmInviteToken(db, demoToken);
        track('account_signed_in', { method: 'email' });
        window.location.reload();
        return true;
      }

      if (mode === 'create') {
        await confirmAccountCode(email.trim(), code.trim());
        await onAccountAuthenticated?.();
        track('account_created', { method: 'email' });
        setCode('');
        setCodeSent(false);
        setJustSignedUp(true);
        setBusy(false);
        return true;
      }
      await confirmSignInCode(db, email.trim(), code.trim());
      track('account_signed_in', { method: 'email' });
      window.location.reload();
      return true;
    } catch (err) {
      setError(readErrorMessage(err));
      setBusy(false);
      return false;
    }
  }, [code, email, mode, db, onAccountAuthenticated]);

  // Web : la page navigue vers Google. Natif : un navigateur in-app s'ouvre et
  // le retour passe par useOAuthReturn. Dans les deux cas rien ne se termine ici.
  //
  // Sauf abandon : si l'utilisateur ferme le navigateur sans aller au bout, aucun
  // retour n'arrive jamais. Sans le listener ci-dessous, `busy` restait vrai pour
  // toujours, ce qui desactive TOUS les boutons de la feuille et lui retire sa
  // fermeture (onClose={busy ? undefined : onClose}) : l'app devenait inutilisable
  // jusqu'au redemarrage. Constate sur iPhone le 30/07/2026.
  //
  // browserFinished se declenche aussi apres un login reussi, mais useOAuthReturn
  // recharge alors l'app dans la foulee : relacher `busy` n'y change rien.
  const continueWithGoogle = useCallback(async () => {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      track('account_google_started');
      if (isNative()) {
        const [{ Browser }, { App: CapApp }] = await Promise.all([
          import('@capacitor/browser'),
          import('@capacitor/app'),
        ]);
        // Deux signaux, parce qu'aucun ne couvre les deux sorties possibles :
        // browserFinished arrive quand l'utilisateur ferme lui-meme le navigateur,
        // le retour au premier plan quand c'est le deep link qui le referme, ce que
        // browserFinished n'emet pas.
        const listeners = [];
        const release = () => {
          setBusy(false);
          listeners.forEach((listener) => listener?.remove?.());
          listeners.length = 0;
        };
        listeners.push(
          await Browser.addListener('browserFinished', release),
          await CapApp.addListener('appStateChange', ({ isActive }) => {
            if (isActive) release();
          })
        );
      }
      if (mode === 'signin') await startGoogleSignIn({ chooseAccount: true });
      else await startGoogleLink();
    } catch (err) {
      setError(readErrorMessage(err));
      setBusy(false);
    }
  }, [mode]);

  // Apple ne quitte jamais l'app : la feuille systeme s'affiche par-dessus et rend son
  // verdict dans le meme appel. Il n'y a donc ni retour a guetter ni `busy` a liberer de
  // loin, contrairement a Google. L'annulation est un rejet comme un autre (code 1001 de
  // AuthenticationServices) : ce n'est pas un echec a montrer en rouge, l'utilisateur
  // vient de refuser lui-meme.
  const continueWithApple = useCallback(async () => {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      track('account_apple_started');
      const outcome = await startAppleSignIn(db);
      if (mode === 'create') await onAccountAuthenticated?.();
      if (outcome === 'signed-in') {
        track('account_signed_in', { method: 'apple' });
        window.location.reload();
        return;
      }
      track('account_created', { method: 'apple' });
      setJustSignedUp(true);
    } catch (err) {
      const raw = typeof err === 'string' ? err : err?.message || '';
      if (!/1001|cancell?ed|canceled by the user/i.test(raw)) {
        setError(readErrorMessage(err, 'Sign in with Apple failed. Please try again.'));
      }
    } finally {
      setBusy(false);
    }
  }, [db, mode, onAccountAuthenticated]);

  const signOut = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      await signOutToGuest(db);
      track('account_signed_out');
      window.location.reload();
    } catch (err) {
      setError(readErrorMessage(err));
      setBusy(false);
    }
  }, [db]);

  return {
    user,
    signedIn,
    mode,
    email,
    setEmail,
    code,
    setCode,
    codeSent,
    busy,
    error,
    setError,
    notice,
    setNotice,
    justSignedUp,
    pendingSignInEmail,
    switchMode,
    startSignIn,
    resetForm,
    resetAll,
    sendCode,
    confirmSignInSwitch,
    cancelSignInSwitch,
    verifyCode,
    continueWithGoogle,
    continueWithApple,
    // Sign in with Apple n'existe qu'en natif iOS ici : le flux web demanderait un
    // Services ID et un secret a regenerer deux fois par an, pour un bouton que
    // personne n'attend dans un navigateur.
    appleAvailable: isNative() && isIOS(),
    signOut,
  };
}

export default useAccountAuth;
